/**
 * Filter field metadata — the global filter bar consumes this.
 * Bot-score buckets follow the brief.
 */

export type EndpointGroup =
  | "auth"
  | "sms"
  | "register"
  | "payments"
  | "messaging"
  | "bookings"
  | "gift_vouchers"
  | "other";

export const ENDPOINT_GROUPS: readonly EndpointGroup[] = [
  "auth",
  "sms",
  "register",
  "payments",
  "messaging",
  "bookings",
  "gift_vouchers",
  "other",
] as const;

export type EndpointAreaKey =
  | "login"
  | "registration"
  | "payments"
  | "messages"
  | "bookings"
  | "gift_vouchers"
  | "other";

export interface EndpointAreaOption {
  key: EndpointAreaKey;
  label: string;
  groups: EndpointGroup[];
}

export const ENDPOINT_AREAS: readonly EndpointAreaOption[] = [
  { key: "login",         label: "Login",                groups: ["auth"] },
  { key: "registration",  label: "Registration",         groups: ["register"] },
  { key: "payments",      label: "Payments",             groups: ["payments"] },
  { key: "messages",      label: "Messages (Email/SMS)", groups: ["messaging", "sms"] },
  { key: "bookings",      label: "Bookings",             groups: ["bookings"] },
  { key: "gift_vouchers", label: "Gift vouchers",        groups: ["gift_vouchers"] },
  { key: "other",         label: "Other",                groups: ["other"] },
] as const;

const GROUP_TO_AREA = new Map<EndpointGroup, EndpointAreaOption>(
  ENDPOINT_AREAS.flatMap((area) => area.groups.map((group) => [group, area] as const)),
);

export function endpointAreaLabel(group: string | null | undefined): string {
  return GROUP_TO_AREA.get(group as EndpointGroup)?.label ?? "Other";
}

export function endpointAreaGroups(group: string | null | undefined): EndpointGroup[] {
  return GROUP_TO_AREA.get(group as EndpointGroup)?.groups ?? ["other"];
}

export function selectedEndpointAreas(groups: readonly EndpointGroup[] = []): EndpointAreaKey[] {
  return ENDPOINT_AREAS
    .filter((area) => area.groups.some((group) => groups.includes(group)))
    .map((area) => area.key);
}

export function groupsForEndpointAreas(keys: readonly EndpointAreaKey[]): EndpointGroup[] {
  const out = new Set<EndpointGroup>();
  for (const key of keys) {
    const area = ENDPOINT_AREAS.find((candidate) => candidate.key === key);
    area?.groups.forEach((group) => out.add(group));
  }
  return [...out];
}

export type Action =
  | "allow"
  | "observe"
  | "challenge_candidate"
  | "block_candidate";

export const ACTIONS: readonly Action[] = [
  "allow",
  "observe",
  "challenge_candidate",
  "block_candidate",
] as const;

export type Outcome = "success" | "failure" | "unknown";

export const OUTCOMES: readonly Outcome[] = [
  "success",
  "failure",
  "unknown",
] as const;

export type ManagedListType =
  | "botnetcc"
  | "malware"
  | "open-proxies"
  | "anonymizer"
  | "vpn";

export const MANAGED_LIST_TYPES: readonly ManagedListType[] = [
  "botnetcc",
  "malware",
  "open-proxies",
  "anonymizer",
  "vpn",
] as const;

export interface BotScoreBucket {
  key: "1-10" | "11-29" | "30-59" | "60-89" | "90-99";
  label: string;
  min: number;
  max: number;
}

export const BOT_SCORE_BUCKETS: readonly BotScoreBucket[] = [
  { key: "1-10",  label: "1–10 (almost certainly automated)", min: 1,  max: 10 },
  { key: "11-29", label: "11–29 (likely automated)",          min: 11, max: 29 },
  { key: "30-59", label: "30–59 (uncertain)",                 min: 30, max: 59 },
  { key: "60-89", label: "60–89 (likely human)",              min: 60, max: 89 },
  { key: "90-99", label: "90–99 (almost certainly human)",    min: 90, max: 99 },
] as const;

/**
 * Validation regexes — used both client-side and (more importantly)
 * server-side in `functions/api/_lib/filters.ts` before any value
 * reaches a SQL template.
 */
export const REGEX = {
  /** ISO 3166-1 alpha-2. */
  country: /^[A-Z]{2}$/,
  /** Cloudflare colo (3 uppercase letters). */
  colo: /^[A-Z]{3}$/,
  /** ASN — positive integer up to 10 digits. */
  asn: /^\d{1,10}$/,
  /** IPv4 or IPv6 (loose). */
  ip: /^[0-9a-fA-F:.%]{2,45}$/,
  /** JA3 hash (32 hex chars). */
  ja3: /^[0-9a-f]{32}$/,
  /** JA4 fingerprint (e.g. "t13d1715h2_5b57614c22b0_3d5424432f57"). */
  ja4: /^[a-z0-9]{1,16}_[A-Za-z0-9_-]{6,128}$/,
  /** Loose email — investigators paste user input; allow anything sane. */
  email: /^[^\s,;'"<>`\\]{3,254}$/,
  /** Business / session IDs — opaque tokens. */
  opaqueId: /^[A-Za-z0-9._-]{4,128}$/,
  /** Sanitised path string. */
  path: /^[A-Za-z0-9._\-/?=&%:]{1,512}$/,
  /** Detection ID — Cloudflare numeric. */
  detectionId: /^\d{1,12}$/,
} as const;
